// libraries
import React, { useState, useId } from 'react'
import clsx from 'clsx'

// components
import RichText from '/react4xp/components/RichText'

// icons
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

export interface MacroAccordionProps {
	theme: 'light' | 'dark'
	heading?: string
	accordion: Array<{
		title: string
		text: string
	}>
}

export default function MacroAccordion({
	theme,
	heading,
	accordion
}: MacroAccordionProps) {

	const [activeItems, setActiveItems] = useState<boolean[]>(new Array(accordion.length).fill(false))

	// unique per instance, the macro can be used several times on the same page
	const instanceId = useId()

	// the trigger carries the section title, so it belongs inside a heading.
	// this is a rich-text macro with no composition point that could pass a
	// level, so it derives one: items sit under the group heading when there
	// is one, and take its place when there is not
	const ItemHeading = isNonWhitespaceString(heading) ? 'h4' : 'h3'

	const toggle = (index: number) => {
		setActiveItems((prevItems) => {
			const updatedItems = [...prevItems]
			updatedItems[index] = !updatedItems[index]
			return updatedItems
		})
	}
	
	return (
		<div className={clsx(
			'macro__accordion',
			theme === 'light' && 'macro__accordion--light'
		)}>

			{isNonWhitespaceString(heading) && (
				<h3 className='accordion-heading'>
					{heading}
				</h3>
			)}

			<div className='accordion-list'>
				{accordion.map((item, i) => {

					const isActive = activeItems[i]
					const titleId = `${instanceId}-title-${i}`
					const panelId = `${instanceId}-panel-${i}`

					return (
						<div
							key={i}
							className={clsx(
								'accordion-list__item',
								isActive && 'accordion-list__item--active'
							)}
						>

							<ItemHeading className='accordion-list__heading'>

								<button
									type='button'
									id={titleId}
									className='accordion-list__title'
									aria-expanded={isActive}
									aria-controls={panelId}
									onClick={() => toggle(i)}
								>

									<span className='text-md'>
										{item.title}
									</span>

									<AngleDown />

								</button>

							</ItemHeading>

							<div
								id={panelId}
								className='accordion-list__text'
								role='region'
								aria-labelledby={titleId}
							>
								<div>
									<div>
										<RichText content={item.text} />
									</div>
								</div>
							</div>

						</div>
					)
				})}
			</div>

		</div>
	)
}